// @flow
import React, {useRef, useEffect, useState} from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import type { Styles } from 'shared/types';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

interface Props {
  allYearsLabel: string,
  activeYear: number,
  years: number[],
  getMorePosts: (page: number, year: number) => void
}

export default function YearFilter(props: Props){
  const c: Styles<typeof styles> = useStyles(props);

  const {
    allYearsLabel,
    activeYear,
    years,
    getMorePosts
  } = props;

  const allYears = [allYearsLabel, ...years]; // like '[All, 2021, 2020, 2019, 2018, 2017]'
  const activeYearIndex = allYears.findIndex(y => y === activeYear);

  const yearListGoBackBtnRef = useRef(); // used for measuring div width of go back button
  const yearListGoNextBtnRef = useRef();// used for measuring div width of go next button
  const yearRefArray = useRef([]); //used to measuing div width of each 'year'
  const containerRef = useRef(); // used to measuing dim width of container.

  const [yearListGoBackBtnWidth, setYearListGoBackBtnWidth] = useState();
  const [yearListGoNextBtnWidth, setYearListGoNextBtnWidth] = useState();
  const [yearWidthArray, setYearWidthArray] = useState(); // like [96, 116, 116, 116, ,116, 116]
  const [containerWidth, setContainerWidth] = useState();
  const [firstYearIndex, setFirstYearIndex] = useState(activeYearIndex >= 0 ? activeYearIndex : 0);
  const [lastYearIndex, setLastYearIndex] = useState();

  useEffect(() => {
    const measureYearWidth = () => {
      // The values must be captured here before calling setXxxState() method.
      const capturedYearWidthArray = yearRefArray.current.map(y => y.offsetWidth);
      const capturedYearListGoBackBtnWdith = yearListGoBackBtnRef.current?.offsetWidth;
      const capturedYearListGoNextBtnWdith = yearListGoNextBtnRef.current?.offsetWidth;

      setYearWidthArray(capturedYearWidthArray);
      setYearListGoBackBtnWidth(capturedYearListGoBackBtnWdith);
      setYearListGoNextBtnWidth(capturedYearListGoNextBtnWdith);
    }

    const measureContainerWidth = () => {
      setContainerWidth(containerRef.current?.offsetWidth);
    }

    const measureWidth = () => {
      measureYearWidth();
      measureContainerWidth();
    }

    window.addEventListener('load', measureWidth)
    window.addEventListener('resize', measureContainerWidth)

    return () => {
      window.removeEventListener('resize', measureContainerWidth);
      window.removeEventListener('load', measureWidth);
    }
  }, []);

  const goAll = () => {
    if (activeYear > 0) {
      getMorePosts(0, -1);
    }
  };

  const goToYear = (year) => {
    if (activeYear !== year) {
      getMorePosts(0, year);
    }
  };

  const goToNext = () => {
    if(yearIndexArray[yearIndexArray.length - 1] < allYears.length - 1){
      setFirstYearIndex(yearIndexArray[yearIndexArray.length - 1] + 1);
      setLastYearIndex(undefined);
    }
  }

  const goToBack = () => {
    if(yearIndexArray[0] > 0){
      setLastYearIndex(yearIndexArray[0] - 1);
    }
  }

  // For example:
  // if allYears is [All, 2021, 2020, 2019, 2018, 2017]
  // and actual first year index is 1 and actual last year idnex is 3.
  // the yearIndexArray will be [1,2,3]
  // the displed years will be ..|2021|2020|2019|...
  let yearIndexArray;
  let actualFirstYearIndex;
  let actualLastYearIndex;

  // check if all elements' width have been measured.
  if(yearWidthArray?.length > 0 && containerWidth > 0 && yearListGoBackBtnWidth >0 && yearListGoNextBtnWidth >0) {
    const fullYearListWidth = yearWidthArray.reduce((a, b) => a + b); // get total lenght of 'year' list

    // check if current pagination width is less than 'year' list with, if yes, that means we need pagination
    if(containerWidth <= fullYearListWidth){
      // restConstainerWidth is the width which hasn't been used by 'year' div, like '2021 |  '
      // at begining, it should be the container width subtract the width of 'back' and 'next' button.
      let restContainerWidth = containerWidth - yearListGoBackBtnWidth - yearListGoNextBtnWidth;

      if(lastYearIndex >= 0){ // if lastYearIndex has value, that means we will calculate the displayed 'year' list by the index of last one in the list
        actualLastYearIndex = lastYearIndex;

        // calculate the first displayed year index by checking if the rest width is enough for next 'year' div, until the rest width is not enough or there is no more year to be displayed.
        for (let i = lastYearIndex; i >= 0; i--){
          if(restContainerWidth >= yearWidthArray[i]){
            actualFirstYearIndex = i;
            restContainerWidth -= yearWidthArray[i];
          }
          else{
            break;
          }
        }

        // if the first displayed year index is 0, and there is enough width still, we will re-calucate the last displayed year index.
        // For example, if first year index is 0, and last year index is 1, currently the pagination div can display 4 'years'
        // we don't want to show them like '...|2021|2020|...', we want  '...|2021|2020|2019|2018|...' to use all room in pagindation div
        // so we need to recalculate last year index.
        if (actualFirstYearIndex === 0){
          for (let i = lastYearIndex + 1; i < allYears.length; i++) {
            if (restContainerWidth >= yearWidthArray[i]) {
              actualLastYearIndex = i;
              restContainerWidth -= yearWidthArray[i];
            }
            else{
              break;
            }
          }
        }
      }else{ // if firstYearIndex has value, calculate the displayed 'year' list by the index of first yesr in the list. the logic is same as above
        actualFirstYearIndex = firstYearIndex;

        for (let i = firstYearIndex; i < allYears.length; i++){
          if(restContainerWidth >= yearWidthArray[i]){
            actualLastYearIndex = i;
            restContainerWidth -= yearWidthArray[i];
          }
          else{
            break;
          }
        }

        if(actualLastYearIndex === allYears.length - 1){
          for (let i = firstYearIndex - 1; i >= 0; i--) {
            if (restContainerWidth >= yearWidthArray[i]) {
              actualFirstYearIndex = i;
              restContainerWidth -= yearWidthArray[i];
            }
            else{
              break;
            }
          }
        }
      }

      // when we have the first and last year index, we can build a array which will be used to render UI.
      yearIndexArray = [];
      if(actualFirstYearIndex >= 0 && actualLastYearIndex >= 0){
        for (let i = actualFirstYearIndex; i <= actualLastYearIndex; i++){
          yearIndexArray.push(i)
        }
      }
    }
  }

  if(!yearIndexArray){ // by default , dispaly all of years.
    yearIndexArray = [];
    for (let i = 0; i < allYears.length; i++){
      yearIndexArray.push(i);
    }
  }

  return (
    <div ref={containerRef} className={c.years}>
      {(!yearListGoBackBtnWidth ||
        (yearIndexArray.length > 0 && yearIndexArray.length < allYears.length)) && (
        <div ref={yearListGoBackBtnRef} className={classNames(c.year, yearIndexArray[0] === 0 && c.disabled) } onClick={goToBack}>
          ...
        </div>
      )}
      {yearIndexArray.map((year, i) => (
        <div
          ref={(e) => (yearRefArray.current[i] = e)}
          className={classNames(
            c.year,
            (activeYear === allYears[year] ||
              (allYears[year] === allYearsLabel && activeYear === -1)) &&
              c.active
          )}
          key={allYears[year]}
          onClick={() => {
            allYears[year] === allYearsLabel ? goAll() : goToYear(allYears[year]);
          }}
        >
          {allYears[year]}
        </div>
      ))}
      {(!yearListGoNextBtnWidth ||
        (yearIndexArray.length > 0 && yearIndexArray.length < allYears.length)) && (
        <div ref={yearListGoNextBtnRef} className={classNames(c.year, yearIndexArray[yearIndexArray.length - 1] >= allYears.length - 1 && c.disabled)} onClick={goToNext} >
          ...
        </div>
      )}
    </div>
  );
}

const styles = {
  years: {
    borderBottom: '1px solid rgb(234, 234, 234)',
    fontSize: '15px',
    fontWeight: '600',
    letterSpacing: '.1em',
    fontFamily: FONT_FAMILY_ROBOTO,
    paddingBottom: '36px',
    textTransform: 'uppercase',
    display: 'flex',
    flexWrap: 'nowrap'
  },
  year: {
    cursor: 'pointer',
    transition: 'all .25s ease-in',
    textTransform: 'uppercase',
    '&:after': {
      content: '"|"',
      marginLeft: '30px',
      marginRight: '30px',
      color: 'black',
    },
    '&:last-child': {
      marginRight: '10px',
      '&:after': {
        display: 'none'
      }
    }
  },
  active: {
    color: '#c9c9c9',
    cursor: 'auto'
  },
  disabled: {
    color: '#c9c9c9',
    cursor: 'auto'
  },
};

const useStyles = createUseStyles(styles);
